import { isItemSurface, skeletonFor, type SkeletonKind } from '@/app/skeleton-routes';
import { m, verbatim, type Message, type PlainKey } from '@/i18n';
import { pathnameOf, standsOver, type TapTarget, type TapTrail } from './tap-mount-store';

/**
 * The shell's loading chrome, the pure half (docs/LOADING.md §4.2): what the
 * top bar and the dock show while a surface loads, decided from what the
 * device already knows (Leon, 2026-09-29: "a skeleton draws every text the
 * device already knows, and a shape stands only for what needs data").
 *
 * The shell draws it, not the skeletons: on a direct visit or a refresh React
 * leaves a streamed Suspense fallback dehydrated while its boundary is
 * pending, so a skeleton's portal or dock claim never runs there. The shell
 * sits outside every pending boundary and hydrates, and its server render is
 * in the first flush. `loading-chrome-ui.tsx` draws what this decides.
 */

/** The names the shell already holds (`AppShell`'s `views` and `groups`). */
export interface ShellNames {
  views: readonly { id: string; name: string }[];
  groups: readonly { id: string; name: string }[];
}

/**
 * The static titles: the exact `pageTitle` each route portals through
 * `TopbarSlot`, known without data, as catalog keys. Keyed by the exact
 * pathname (a trailing slash dropped); a deeper route under one of these finds
 * no entry here and is named by `knownTitleFor`'s later rules, or not at all.
 */
const STATIC_TITLES: Record<string, PlainKey> = {
  '/work': 'shell.page.work',
  '/activity': 'shell.page.activity',
  '/boards': 'shell.page.views',
  '/views': 'shell.page.lenses',
  '/groups': 'shell.page.groups',
  '/flows': 'shell.page.flows',
  '/flow-phases': 'shell.page.flowPhases',
  '/plans': 'shell.page.plans',
  '/ceremonies': 'shell.page.ceremonies',
  '/tags': 'shell.page.tags',
  '/search': 'shell.page.search',
  '/map': 'shell.page.map',
  '/account': 'shell.page.account',
  '/account/contacts': 'shell.page.contacts',
  '/design': 'shell.page.design',
};

/** Routes that portal nothing into the top bar (the auth and invite pages):
 *  the chrome draws no title there, since nothing would ever replace it. */
const NO_TITLE = /^\/(signin|signup|reset-password|verify-email|invite|join|connect)(\/|$)/;

/**
 * The title a route shows, known without reading its data: the static name,
 * a view's or a group's name from the shell, or the tapped row's title for
 * the target it named. A descriptor the surface formats: a static name is its
 * key, a name the shell or the tap holds is shown as it is (`verbatim`). Null
 * when only the route's data knows it (the chrome then draws a title-sized
 * shape).
 */
export function knownTitleFor(
  pathname: string,
  tapTarget: TapTarget | null,
  shell: ShellNames,
): Message | null {
  const path = pathname.length > 1 ? pathname.replace(/\/$/, '') : pathname;
  const fixed = STATIC_TITLES[path];
  if (fixed) return m(fixed);
  // The admin layout portals one title for every admin tab.
  if (path === '/admin' || path.startsWith('/admin/')) return m('shell.page.admin');
  const view = /^\/(views|boards)\/([^/]+)$/.exec(path);
  if (view) {
    const name = shell.views.find((v) => v.id === decodeURIComponent(view[2]!))?.name;
    if (name) return verbatim(name);
  }
  const group = /^\/groups\/([^/]+)$/.exec(path);
  if (group) {
    const name = shell.groups.find((g) => g.id === decodeURIComponent(group[1]!))?.name;
    if (name) return verbatim(name);
  }
  if (tapTarget?.title && pathnameOf(tapTarget.href) === path) return verbatim(tapTarget.title);
  return null;
}

/** What the top bar's default seat draws. */
export type LoadingTitle =
  /** A known title, in the page title's face. */
  | { kind: 'text'; text: Message }
  /** An item surface's trail, from the tap's store. */
  | { kind: 'trail'; trail: TapTrail }
  /** An item surface whose trail the store does not hold: a line in the
   *  trail's geometry. */
  | { kind: 'trail-shape' }
  /** A title only the route's data knows: a title-sized line. */
  | { kind: 'shape' }
  /** A route that portals no title. */
  | { kind: 'none' };

export interface LoadingChrome {
  /** The pathname the chrome speaks for: the tap's target while the tap's
   *  mount stands, the current pathname otherwise. */
  shown: string;
  kind: SkeletonKind;
  /** Whether the tap's mount stands over the current route. */
  standing: boolean;
  title: LoadingTitle;
  /** The tap's mount stands for a page (not an item surface): its title
   *  takes the top bar over the old page's header and over a peek's trail,
   *  which both stay mounted until the router commits. */
  overridesBar: boolean;
  /** The dock row to draw in place of the nav lanes and the action slot:
   *  the item's loading row, a section's back-only dock, or nothing. */
  dock: 'item' | 'section' | null;
  /** The target the chrome reads the title, trail and Back fallback from:
   *  the tap's, when it names the pathname shown. */
  target: TapTarget | null;
}

/**
 * The decision. The top bar's default seat shows `title` whenever no page
 * has filled the slot (the shell hides it in CSS once a portal fills it), and
 * over everything while `overridesBar`. The dock row stands while the tap's
 * mount stands for an item surface (over whatever the page below claimed),
 * and while the pathname is an item surface whose page has not claimed the
 * dock's nav region yet (a route skeleton, a direct visit).
 */
export function loadingChromeFor({
  pathname,
  tapTarget,
  navClaimed,
  shell,
}: {
  pathname: string;
  tapTarget: TapTarget | null;
  navClaimed: boolean;
  shell: ShellNames;
}): LoadingChrome {
  const standing = standsOver(tapTarget, pathname);
  const shown = standing ? pathnameOf(tapTarget.href) : pathname;
  const kind = skeletonFor(shown);
  const target = tapTarget !== null && pathnameOf(tapTarget.href) === shown ? tapTarget : null;
  const item = isItemSurface(kind);

  let title: LoadingTitle;
  if (item) {
    title = target?.trail ? { kind: 'trail', trail: target.trail } : { kind: 'trail-shape' };
  } else if (NO_TITLE.test(shown)) {
    title = { kind: 'none' };
  } else {
    const text = knownTitleFor(shown, target, shell);
    title = text ? { kind: 'text', text } : { kind: 'shape' };
  }

  const dock = item && (standing || !navClaimed) ? kind : null;
  return { shown, kind, standing, title, overridesBar: standing && !item, dock, target };
}
